import type { CanvasContext, DrawingContext } from '@studio/design-system/canvas';
import type { Coordinate } from '@studio/js-utils/geometry';
import type { Device } from '@studio/models/device';
import { useLatestFn, useShallowEffect } from '@studio/react-utils/hooks';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { noop } from 'lodash-es';
import type { RefObject } from 'react';
import { useRef, useState } from 'react';
// eslint-disable-next-line import/no-internal-modules
import { getLastKnownPosition } from '../cursor/cursor.jsx';
import {
  setFastFrameInitialScale,
  setFastFrameOnExitTool,
  setToolInitialPosition,
  useToolInitialPosition
} from '../fast-frame/hooks.js';
import { CursorSelectedDuration, Scale } from './constants.js';
import type { Wiring } from './types.js';
import { drawCursor } from './utils.js';
import { useAnalyticsTrack } from '#~/analytics/spam-protection/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES, MOVEMENT_TOOLS_PROPERTIES } from '#~/analytics/index.js';
import { useAutoTrackingControlProtection } from '#~/auto-tracking/control-protection/hooks.js';
import { Command } from '#~/command/index.js';
import { useToolPref, useToolVariants } from '#~/control/hooks.js';
import { Tool } from '#~/control/index.js';
import { moveTo } from '#~/control/movement/commands/index.js';

/**
 * Fast frame wiring.
 *
 * @param deviceId - Device ID.
 * @param canvasRef - Cursor canvas reference.
 * @returns Fast frame wiring.
 */
export const useWiring = (
  deviceId: Device['id'],
  canvasRef: RefObject<CanvasContext | null>
): Wiring => {
  const [clickToCenterSelectPosition, setClickToCenterSelectPosition] =
    useState<T_Maybe<Coordinate>>(undefined);

  const [selected, setSelected] = useState<boolean>(false);

  const [_, setTool] = useToolPref();
  const track = useAnalyticsTrack();

  const [canvasDimensions, setCanvasDimensions] = useState<{
    canvasHeight: number;
    canvasWidth: number;
  }>({
    canvasHeight: 0,
    canvasWidth: 0
  });

  const statePosition = useToolInitialPosition();
  const initialPosition = useRef(statePosition).current ?? getLastKnownPosition();

  const [variants] = useToolVariants();
  const useCine = variants[Tool.Center] === Command.TOOL_CINE_CENTER;

  const [clickSelectionTimer, setClickSelectionTimer] = useState<number>();

  const { isProtected, triggerAlert: triggerControlAlert } =
    useAutoTrackingControlProtection(deviceId) ?? {};

  const handleClickEvent = useLatestFn((_event: React.MouseEvent): void => {
    if (!clickToCenterSelectPosition) {
      return;
    }

    let { x, y } = clickToCenterSelectPosition;

    const { canvasHeight, canvasWidth } = canvasDimensions;

    x /= canvasWidth;
    y /= canvasHeight;

    setSelected(true);
    clearTimeout(clickSelectionTimer);

    const timeout = window.setTimeout(() => {
      setSelected(false);
    }, CursorSelectedDuration);

    setClickSelectionTimer(timeout);

    if (isProtected) {
      triggerControlAlert?.(() => {
        moveTo(
          deviceId,
          {
            center: { x, y },
            scale: 0
          },
          useCine,
          { toolUsed: useCine ? 'cine_center' : 'click_to_center' }
        ).catch(noop);
      });

      return;
    }

    moveTo(
      deviceId,
      {
        center: { x, y },
        scale: 0
      },
      useCine,
      { toolUsed: useCine ? 'cine_center' : 'click_to_center' }
    )
      .then(() => {
        // Track the click-to-center shot event
        track(EVENT_NAMES.CLICK_TO_CENTER_SHOT, EVENT_CATEGORIES.MOVEMENT_TOOLS, {
          [MOVEMENT_TOOLS_PROPERTIES.SHOT_TYPE]: useCine ? 'cine' : 'standard',
          x: x.toFixed(4),
          y: y.toFixed(4)
        });
      })
      .catch(noop);
  });

  const handleDrawCursor = useLatestFn((context: DrawingContext, position?: Coordinate): void => {
    setClickToCenterSelectPosition(position ?? { x: -100, y: -100 });

    const canvasWidth = context.surface.canvas.width / devicePixelRatio;
    const canvasHeight = context.surface.canvas.height / devicePixelRatio;

    setCanvasDimensions({ canvasHeight, canvasWidth });

    drawCursor(
      {
        ...context,
        clickToCenterSelectPosition: position,
        scale: Scale,
        selected
      },
      position,
      useCine
    );
  });

  const handleWheelEvent = useLatestFn((_event: React.WheelEvent): void => {
    setToolInitialPosition(clickToCenterSelectPosition);
    setFastFrameInitialScale(Scale);
    setFastFrameOnExitTool(Tool.Center);
    setTool(Tool.FastFrame);
  });

  const handleRedraw = useLatestFn(() => {
    canvasRef.current?.draw();
  });

  // Redraw cursor if necessary.
  useShallowEffect(() => {
    handleRedraw();
  }, [selected, initialPosition]);

  return {
    handleClickEvent,
    handleDrawCursor,
    handleWheelEvent,
    initialPosition,
    useCine
  };
};
